import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getOtherPayees, saveOtherPayee } from "../../components/Api/GmsOtherPayee";
import { SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const defaultForm = {
  OtherPayee_Id: 0,
  OtherPayee_Name: "",
  OtherPayee_Details: "",
  OtherPayee_CreatedDate: new Date().toISOString(),
  OtherPayee_CreatedBy: 1,
  OtherPayee_ModifiedDate: new Date().toISOString(),
  OtherPayee_ModifiedBy: 1,
};

const emptyFilter = {
  OtherPayee_Id: null,
  OtherPayee_Name: "",
};

export default function OtherPayeePage() {
  const [otherPayees, setOtherPayees] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsOtherPayee');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsOtherPayee');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsOtherPayee');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsOtherPayee');
  const isAdmin = isUserAdmin();

  const formatFullDateTime = (dateTimeStr) => {
    try {
      if (!dateTimeStr) return "N/A";
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    if (hasPermission) {
      loadOtherPayees();
      if (isViewOnly) {
        setActiveTab("2");
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadOtherPayees();
    }
  }, [hasAppliedFilter]);

  const loadOtherPayees = async (filterData = filter) => {
    setLoading(true);
    try {
      const data = await getOtherPayees(filterData);
      // Map camelCase response to PascalCase for consistency
      const mappedData = Array.isArray(data) ? data.map(item => ({
        OtherPayee_Id: item.otherPayee_Id || item.OtherPayee_Id,
        OtherPayee_Name: item.otherPayee_Name || item.OtherPayee_Name,
        OtherPayee_Details: item.otherPayee_Details || item.OtherPayee_Details,
        OtherPayee_CreatedDate: item.otherPayee_CreatedDate || item.OtherPayee_CreatedDate,
        OtherPayee_CreatedBy: item.otherPayee_CreatedBy || item.OtherPayee_CreatedBy,
        OtherPayee_ModifiedDate: item.otherPayee_ModifiedDate || item.OtherPayee_ModifiedDate,
        OtherPayee_ModifiedBy: item.otherPayee_ModifiedBy || item.OtherPayee_ModifiedBy,
      })) : [];
      setOtherPayees(mappedData);
      setTotalCount(mappedData.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load other payees: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.OtherPayee_Name || !form.OtherPayee_Name.trim()) {
      Swal.fire("Error", "Please enter other payee name", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();
      const otherPayeeData = {
        ...form,
        OtherPayee_CreatedBy: isEditing ? form.OtherPayee_CreatedBy : session.userId,
        OtherPayee_ModifiedBy: session.userId,
        OtherPayee_ModifiedDate: new Date().toISOString(),
      };

      const result = await saveOtherPayee(otherPayeeData);
      
      if (result.id) {
        Swal.fire("Success", result.message || "Other payee saved successfully!", "success");
        handleClear();
        if (hasAppliedFilter) {
          loadOtherPayees();
        } else {
          loadOtherPayees(emptyFilter);
        }
      }
    } catch (error) {
      let errorMessage = "Failed to save other payee";
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
          errorMessage = match ? match[1].trim() : error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (otherPayee) => {
    setEditLoadingRow(otherPayee.OtherPayee_Id);
    try {
      setForm({
        OtherPayee_Id: otherPayee.OtherPayee_Id,
        OtherPayee_Name: otherPayee.OtherPayee_Name || "",
        OtherPayee_Details: otherPayee.OtherPayee_Details || "",
        OtherPayee_CreatedDate: otherPayee.OtherPayee_CreatedDate,
        OtherPayee_CreatedBy: otherPayee.OtherPayee_CreatedBy,
        OtherPayee_ModifiedDate: otherPayee.OtherPayee_ModifiedDate,
        OtherPayee_ModifiedBy: otherPayee.OtherPayee_ModifiedBy,
      });
      setIsEditing(true);
      setActiveTab("1");
      window.scrollTo(0, 0);
    } catch (error) {
      Swal.fire("Error", "Failed to load other payee data", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleClear = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadOtherPayees(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(false);
    setPage(1);
    loadOtherPayees(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getPaginatedOtherPayees = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return otherPayees.slice(startIndex, endIndex);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      if (tab === "2") {
        loadOtherPayees();
      }
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Other Payee Management" breadcrumbItem="Payee" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {!isViewOnly && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Other Payee Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Other Payee List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  {!isViewOnly && (
                    <TabPane tabId="1">
                      <div>
                        <Form onSubmit={handleSubmit}>
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <Label for="OtherPayee_Name">Name <span className="text-danger">*</span></Label>
                                <Input
                                  type="text"
                                  id="OtherPayee_Name"
                                  value={form.OtherPayee_Name}
                                  onChange={(e) => handleFormChange("OtherPayee_Name", e.target.value)}
                                  placeholder="Enter other payee name"
                                  required
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label for="OtherPayee_Details">Details</Label>
                                <Input
                                  type="text"
                                  id="OtherPayee_Details"
                                  value={form.OtherPayee_Details}
                                  onChange={(e) => handleFormChange("OtherPayee_Details", e.target.value)}
                                  placeholder="Enter details"
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row className="mt-4">
                            <Col>
                              <div className="d-flex gap-2">
                                <Button
                                  type="submit"
                                  color="primary"
                                  disabled={saveLoading}
                                  className="waves-effect waves-light"
                                >
                                  {saveLoading && (
                                    <ClipLoader size={16} color="#ffffff" className="me-2" />
                                  )}
                                  {isEditing ? "Update Other Payee" : "Save Other Payee"}
                                </Button>
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={handleClear}
                                  className="waves-effect waves-light"
                                >
                                  Clear
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Form>
                      </div>
                    </TabPane>
                  )}
                  <TabPane tabId="2">
                    <div>
                      {/* Filter Section */}
                      <Card>
                        <CardHeader>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">Filter Other Payees</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                        </CardHeader>
                        {showFilters && (
                          <CardBody>
                            <Row>
                              <Col md={4}>
                                <FormGroup>
                                  <Label for="filter_OtherPayee_Name">Name</Label>
                                  <Input
                                    type="text"
                                    id="filter_OtherPayee_Name"
                                    value={filter.OtherPayee_Name}
                                    onChange={(e) => handleFilterChange("OtherPayee_Name", e.target.value)}
                                    placeholder="Enter other payee name"
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={12}>
                                <div className="d-flex justify-content-end">
                                  <Button color="secondary" type="button" onClick={handleClearFilter} className="me-2">
                                    Clear Filter
                                  </Button>
                                  <Button color="primary" type="button" onClick={handleApplyFilter}>
                                    Apply Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          </CardBody>
                        )}
                      </Card>

                      {/* Other Payees Table */}
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Other Payee List ({totalCount} records)
                            </h5>
                            <div className="d-flex align-items-center">
                              <Label className="me-2">Show:</Label>
                              <Input
                                type="select"
                                value={pageSize}
                                onChange={(e) => {
                                  setPageSize(parseInt(e.target.value));
                                  setPage(1);
                                }}
                                style={{ width: "auto" }}
                              >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                              </Input>
                            </div>
                          </div>
                          
                          {loading ? (
                            <div className="text-center">
                              <ClipLoader size={50} color="black" />
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table bordered>
                                  <thead className="table-light">
                                    <tr>
                                      <th>Sr.No</th>
                                      <th>Name</th>
                                      <th>Details</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {otherPayees.length === 0 ? (
                                      <tr>
                                        <td colSpan="4" className="text-center">
                                          No other payees found
                                        </td>
                                      </tr>
                                    ) : (
                                      getPaginatedOtherPayees().map((otherPayee, index) => (
                                        <tr key={otherPayee.OtherPayee_Id}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{otherPayee.OtherPayee_Name}</td>
                                          <td>{otherPayee.OtherPayee_Details || "N/A"}</td>
                                          <td>
                                            {!isViewOnly && (
                                              <div className="d-flex gap-2">
                                                <Button
                                                  color="info"
                                                  size="sm"
                                                  onClick={() => handleEdit(otherPayee)}
                                                  disabled={editLoadingRow === otherPayee.OtherPayee_Id}
                                                >
                                                  {editLoadingRow === otherPayee.OtherPayee_Id ? (
                                                    <ClipLoader size={16} color="white" />
                                                  ) : (
                                                    "Edit"
                                                  )}
                                                </Button>
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </Table>
                              </div>

                              {/* Pagination */}
                              <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                                   style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                                <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                                  <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                                  Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                                </div>
                                {totalCount > pageSize && (
                                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === 1}
                                      onClick={() => handlePageChange(page - 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                        color: page === 1 ? '#6c757d' : '#495057'
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-left"></i>
                                    </Button>
                                    
                                    {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                      const pageNum = index + 1;
                                      const isActive = page === pageNum;
                                      
                                      return (
                                        <Button
                                          key={pageNum}
                                          color={isActive ? "primary" : "light"}
                                          size="sm"
                                          onClick={() => handlePageChange(pageNum)}
                                          style={{
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.375rem',
                                            minWidth: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: isActive ? '#556ee6' : '#fff',
                                            borderColor: isActive ? '#556ee6' : '#dee2e6',
                                            color: isActive ? '#fff' : '#495057',
                                            fontWeight: isActive ? '600' : '400',
                                            boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                          }}
                                        >
                                          {pageNum}
                                        </Button>
                                      );
                                    })}
                                    
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === Math.ceil(totalCount / pageSize)}
                                      onClick={() => handlePageChange(page + 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                        color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-right"></i>
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

